import fromUnixTime from 'date-fns/fromUnixTime';
import differenceInDays from 'date-fns/differenceInDays';
import Cookies from 'js-cookie';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { emitter } from 'shared/helpers/mitt';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../constants/appEvents';

Cookies.defaults = { sameSite: 'Lax' };

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  emitter.emit(CHATWOOT_SET_USER, { user });
  emitter.emit(ANALYTICS_IDENTITY, { user });
};

export const getHeaderExpiry = response =>
  fromUnixTime(response.headers.expiry);

export const setAuthCredentials = response => {
  const expiryDate = getHeaderExpiry(response);
  Cookies.set('cw_d_session_info', JSON.stringify(response.headers), {
    expires: differenceInDays(expiryDate, new Date()),
  });
  setUser(response.data.data, expiryDate);
};

// O `uid` do devise_token_auth e o proprio e-mail. Quando o usuario troca o
// e-mail no perfil, o `uid` muda no servidor e o que esta guardado no cookie
// (lido uma unica vez, no boot, para os headers padrao do axios) fica velho —
// a proxima chamada levaria o uid antigo e voltaria 401. A resposta do PUT ja
// traz os headers novos; basta grava-los. Ver APIHelper.js.
export const refreshAuthHeaders = response => {
  const headers = response?.headers || {};
  if (!headers.uid || !headers['access-token']) return;

  const stored = (() => {
    try {
      return JSON.parse(Cookies.get('cw_d_session_info') || '{}');
    } catch (error) {
      return {};
    }
  })();

  const merged = {
    ...stored,
    'access-token': headers['access-token'],
    'token-type': headers['token-type'] || stored['token-type'],
    client: headers.client || stored.client,
    expiry: headers.expiry || stored.expiry,
    uid: headers.uid,
  };

  try {
    Cookies.set('cw_d_session_info', JSON.stringify(merged), {
      expires: differenceInDays(fromUnixTime(merged.expiry), new Date()),
    });
  } catch (error) {
    // Cookie indisponivel: os headers em memoria abaixo ainda seguram a sessao
    // desta aba, e o proximo login regrava o cookie.
  }

  const common = window.axios?.defaults?.headers?.common;
  if (common) {
    common['access-token'] = merged['access-token'];
    common['token-type'] = merged['token-type'];
    common.client = merged.client;
    common.expiry = merged.expiry;
    common.uid = merged.uid;
  }
};

export const clearBrowserSessionCookies = () => {
  const cookieNames = ['cw_d_session_info', 'auth_data', 'user'];
  const hostname = window.location.hostname || '';
  const parts = hostname.split('.');
  const baseDomain = parts.length > 2 ? parts.slice(-2).join('.') : hostname;
  const removalOptions = [
    undefined,
    { path: '/' },
    { path: '/', domain: hostname },
    { path: '/', domain: `.${baseDomain}` },
    { path: '/', domain: baseDomain },
    // Cookie particionado (CHIPS) so e apagado por um Set-Cookie que tambem
    // traga `Partitioned`: para o navegador, o particionado e o comum sao
    // duas entradas distintas. Desde 01/09/2026 os cookies de auth sao
    // `SameSite=None; Secure; Partitioned` (session_store.rb e
    // browser_sso_controller.rb), entao sem esta variante a limpeza inteira
    // vira no-op e o dashboard e o /app/login ficam se devolvendo um ao outro.
    // As variantes acima continuam para os cookies gravados antes disso.
    { path: '/', secure: true, sameSite: 'none', partitioned: true },
  ];
  cookieNames.forEach(name => {
    removalOptions.forEach(options => {
      try {
        if (options) {
          Cookies.remove(name, options);
        } else {
          Cookies.remove(name);
        }
      } catch (e) {
        // ignore - best-effort removal across domain/path variants
      }
    });
  });
};

export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
  // Aceleres: sair de verdade apaga a memoria do ultimo modulo — é isso que
  // faz o proximo login abrir na tela padrao.
  LocalStorage.remove(LOCAL_STORAGE_KEYS.LAST_MODULE);
};

// Aceleres: no Zoraxy a entrada normal do usuario é um SSO vindo do Aceleres,
// ou seja, TODO acesso passa pelo login — inclusive só reabrir a aba. Por isso
// o login limpa apenas o que o upstream sempre limpou (rascunhos) e preserva a
// memoria do ultimo modulo; quem apaga essa memoria é o logout.
export const clearLocalStorageOnLogin = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
};

export const clearSessionStorageOnLogout = () => {
  SessionStorage.remove(SESSION_STORAGE_KEYS.IMPERSONATION_USER);
};

export const deleteIndexedDBOnLogout = async () => {
  let dbs = [];
  try {
    dbs = await window.indexedDB.databases();
    dbs = dbs.map(db => db.name);
  } catch (e) {
    dbs = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
  }

  dbs.forEach(dbName => {
    const deleteRequest = window.indexedDB.deleteDatabase(dbName);

    deleteRequest.onerror = event => {
      // eslint-disable-next-line no-console
      console.error(`Error deleting database ${dbName}.`, event);
    };

    deleteRequest.onsuccess = () => {
      // eslint-disable-next-line no-console
      console.log(`Database ${dbName} deleted successfully.`);
    };
  });

  localStorage.removeItem('cw-idb-names');
};

const currentLogoutTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const withLogoutTime = target => {
  try {
    const url = new URL(target, window.location.origin);
    if (
      url.pathname === '/session-ended' ||
      (url.pathname === '/login' &&
        url.searchParams.has('aceleres_session_ended'))
    ) {
      url.searchParams.set('logout_at', currentLogoutTime());
    }
    return url.toString();
  } catch (e) {
    return target;
  }
};

export const redirectForAceleresSessionEnd = () => {
  if (window.aceleresSessionTerminationStarted) return;

  window.aceleresSessionTerminationStarted = true;
  emitter.emit(CHATWOOT_RESET);
  emitter.emit(ANALYTICS_RESET);
  clearBrowserSessionCookies();
  clearLocalStorageOnLogout();
  clearSessionStorageOnLogout();
  deleteIndexedDBOnLogout();

  const logoutBridge = window.globalConfig?.LOGOUT_REDIRECT_LINK || '/';
  let returnUrl = logoutBridge;

  try {
    const bridgeUrl = new URL(logoutBridge, window.location.origin);
    bridgeUrl.searchParams.set('source', 'acelerity-sync');
    bridgeUrl.searchParams.set('logout_at', currentLogoutTime());
    returnUrl = bridgeUrl.toString();
  } catch (e) {
    // Keep the safe local fallback if the configured bridge is malformed.
    returnUrl = '/';
  }

  const logoutUrl = `/internal/sso/logout?return=${encodeURIComponent(returnUrl)}`;
  window.location.assign(logoutUrl);
};

export const clearCookiesOnLogout = () => {
  if (window.aceleresSessionTerminationStarted) return;

  window.aceleresSessionTerminationStarted = true;
  emitter.emit(CHATWOOT_RESET);
  emitter.emit(ANALYTICS_RESET);
  clearBrowserSessionCookies();
  clearLocalStorageOnLogout();
  clearSessionStorageOnLogout();
  const globalConfig = window.globalConfig || {};
  const logoutRedirectLink = globalConfig.LOGOUT_REDIRECT_LINK || '/';
  window.location = withLogoutTime(logoutRedirectLink);
};

export const parseAPIErrorResponse = error => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  }
  if (error?.response?.data?.error) {
    return error?.response?.data?.error;
  }
  if (error?.response?.data?.errors) {
    return error?.response?.data?.errors[0];
  }
  return error;
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  throw new Error(errorMessage);
};

export const parseLinearAPIErrorResponse = (error, defaultMessage) => {
  const errorData = error.response.data;
  const errorMessage = errorData?.error?.errors?.[0]?.message || defaultMessage;
  return errorMessage;
};
